import i18n from '@/plugins/vue-i18n';
import { RouteRecordRaw } from 'vue-router';
import { Actions, Subjects } from '@boqnet/permissions';
import { useFeatureFlagsStore } from '@/stores/feature-flags.store';
import { usePermissionStore } from '@/stores/permission.store';
import { useBulkOrderCapability } from '@/composables/use-bulk-order-capability';
import { selectedOrgId } from '@/common/utils/role.utils';

const { t } = i18n.global;

export default [
  // Orders
  {
    path: '/:organisationId?/orders',
    component: () => import('@/views/orders/OrdersLayout.vue'),
    meta: {
      title: t('routes.orders.ordersPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.LIST,
      subject: Subjects.ORDER,
    },
    children: [
      // Orders Tab
      {
        path: 'orders',
        name: 'orders',
        component: () => import('@/views/accounts/account-details-view/tabs/OrdersModule.vue'),
      },
      // Returns Tab
      {
        path: 'returns',
        name: 'returns',
        component: () => import('@/views/accounts/account-details-view/tabs/ReturnsModule.vue'),
      },
      // Bulk Orders Tab
      {
        // Context: the name must be literally `bulk-orders` — `OrdersLayout.vue` resolves the
        // active tab by matching `tab.id === route.name` and navigates with
        // `router.push({ name: id })`, so the tab id and the route name are one value.
        path: 'bulk-orders',
        name: 'bulk-orders',
        component: () => import('@/views/orders/tabs/BulkOrdersModule.vue'),
        meta: {
          title: t('routes.orders.bulkOrdersPageTitle'),
          layout: 'default',
          requiresAuth: true,
          // `anyOf` overrides the parent's LIST:ORDER, narrowing this child to the bulk subject
          // (`accounts-credits` is the working precedent). `router/index.ts` checks it once the
          // ability is initialised; `beforeEnter` below adds the flag and the cold-load hold.
          anyOf: [{ action: Actions.LIST, subject: Subjects.BULK_ORDER }],
        },
        /**
         * Context: the tab is reachable by direct URL regardless of whether `OrdersLayout` renders
         * its button, so the flag and the capability are enforced here as well as there — two
         * seams, not one.
         *
         * `beforeEnter` as well as route meta because meta cannot read the flag, and
         * `router/index.ts` skips the meta check until the ability is initialised (DL-46). A
         * route-level function is evaluated at navigation time and can therefore call `useCan()`
         * and the flag store. Chosen over the `redirect` variant
         * (`accounts.routes.ts`) because this is a conditional refusal rather than an
         * unconditional rewrite, and `redirect` has no "carry on" return value.
         */
        beforeEnter: () => {
          // The same composable the tab's own visibility reads, so the button and the URL can
          // never disagree about who may be here.
          const { canListBulkOrders, isResolved } = useBulkOrderCapability();

          /**
           * Nothing is refused until the answer is final. The wizard route below holds on fewer
           * markers: it doesn't wait for the boot-time role refresh (DL-224 C-06).
           *
           * Context: a cold load (a reload, a new tab, the placement-incomplete email's hub link)
           * runs this before LaunchDarkly and the boot-time role refresh have settled. Refusing then
           * dropped the user on Orders with the link's query gone, and nothing brought them back
           * (PE-1081). Letting it through shows nothing: `BulkOrdersModule` renders no content and
           * sends no request until access is confirmed, and leaves for Orders if it is refused.
           */
          if (!isResolved.value) return true;

          const { bulkOrdersEnabled } = useFeatureFlagsStore();

          if (!bulkOrdersEnabled || !canListBulkOrders.value) {
            return {
              name: 'orders',
              params: selectedOrgId.value ? { organisationId: selectedOrgId.value } : {},
            };
          }

          // Returned rather than passed to `next()`: vue-router 5 deprecates the callback form, and
          // the rest of this router predates that. New guards take the return form.
          return true;
        },
      },
      {
        path: '',
        redirect: { name: 'orders' },
      },
    ],
  },
  // Create bulk order (the four-step wizard)
  {
    /**
     * Context: a **top-level** route rather than a child of `OrdersLayout`, mirroring how
     * `create-new-order` sits outside the tabbed orders layout — the wizard owns the whole frame
     * (fixed header + stepper, scrolling body, pinned footer) and must not inherit the tab strip.
     */
    path: '/:organisationId?/orders/create-bulk-order',
    component: () => import('@/views/bulk-orders/CreateBulkOrderShell.vue'),
    meta: {
      title: t('routes.orders.createBulkOrderPageTitle'),
      layout: 'default',
      requiresAuth: true,
      /**
       * `Create` on `Subjects.BULK_ORDER`, checked by `router/index.ts` once the ability is
       * initialised. `beforeEnter` below adds the flag, and the shell its own `useCan()` check
       * (DL-46).
       */
      action: Actions.CREATE,
      subject: Subjects.BULK_ORDER,
    },
    /**
     * Context: `beforeEnter` rather than `redirect`, following the Bulk Orders tab above for the
     * same stated reason — this is a conditional refusal, not an unconditional rewrite, and
     * `redirect` has no "carry on" return value. A route-level *function* is evaluated at
     * navigation time and can therefore call `useCan()`, which resolves through whichever
     * permission engine is live.
     */
    beforeEnter: () => {
      /**
       * Nothing is refused until the answer is authoritative.
       *
       * Context: LaunchDarkly resolves after bootstrap, so on a cold navigation the bulk-orders
       * flag and the permission-engine flag both read their defaults for a tick, and
       * `useBulkOrderCapability` may answer through the wrong engine. Denying in that window sent
       * a legitimate deep link to the fallback and never brought it back when the flags arrived,
       * which broke the session side-car's whole purpose — a reload landed on Orders instead of
       * the wizard. `isInitialized` and `areRoleFlagsResolved` are the platform's existing
       * markers for "this ability is authoritative"; `permission.store.ts` says in terms that
       * they are what the router reads *before it is willing to deny*.
       *
       * Letting the navigation through costs nothing: the wizard shell renders no content until
       * its own `isPermitted` check passes, and its watch takes the user out the moment access
       * resolves to no. So the unresolved window shows chrome and nothing else, either way.
       *
       * Unlike the Bulk Orders tab, it doesn't also wait for the boot-time role refresh
       * (`useBulkOrderCapability().isResolved`). A reload mid-wizard comes from a user who
       * already held the role in the stored session (DL-224 C-06).
       */
      const permissionStore = usePermissionStore();
      if (!permissionStore.isInitialized || !permissionStore.areRoleFlagsResolved) return true;

      const { bulkOrdersEnabled } = useFeatureFlagsStore();
      // The create tier, not the list tier: reaching the wizard is a create question. Asked per
      // project inside the composable, because the two engines answer an unscoped create
      // differently.
      const { canCreateBulkOrder } = useBulkOrderCapability();

      if (!bulkOrdersEnabled || !canCreateBulkOrder.value) {
        return {
          name: 'bulk-orders',
          params: selectedOrgId.value ? { organisationId: selectedOrgId.value } : {},
        };
      }

      return true;
    },
    children: [
      {
        path: 'items',
        name: 'create-bulk-order-items',
        component: () => import('@/components/BulkOrders/Create/Steps/ItemsStep.vue'),
        meta: { title: t('routes.orders.createBulkOrderItemsPageTitle') },
      },
      {
        path: 'sites',
        name: 'create-bulk-order-sites',
        component: () => import('@/components/BulkOrders/Create/Steps/SitesStep.vue'),
        meta: { title: t('routes.orders.createBulkOrderSitesPageTitle') },
      },
      {
        path: 'allocate',
        name: 'create-bulk-order-allocate',
        component: () => import('@/components/BulkOrders/Create/Steps/AllocateStep.vue'),
        meta: { title: t('routes.orders.createBulkOrderAllocatePageTitle') },
      },
      {
        path: 'payment',
        name: 'create-bulk-order-payment',
        component: () => import('@/components/BulkOrders/Create/Steps/PaymentStep.vue'),
        meta: { title: t('routes.orders.createBulkOrderPaymentPageTitle') },
      },
      {
        path: 'review',
        name: 'create-bulk-order-review',
        component: () => import('@/components/BulkOrders/Create/Steps/ReviewStep.vue'),
        meta: { title: t('routes.orders.createBulkOrderReviewPageTitle') },
      },
      {
        path: '',
        redirect: { name: 'create-bulk-order-items' },
      },
    ],
  },
  {
    path: '/:organisationId?/orders/create-new-order',
    name: 'create-new-order',
    component: () => import('@/views/orders/CreateOrderViewRework.vue'),
    meta: {
      title: t('routes.orders.createNewPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.LIST,
      subject: Subjects.ORDER,
    },
  },
  {
    path: '/:organisationId?/orders/create-new-order/confirm-requests',
    name: 'confirm-order-requests',
    component: () => import('@/views/projects/CreateOrderRequests.vue'),
    meta: {
      title: t('routes.projects.confirmRequestsPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.LIST,
      subject: Subjects.ORDER,
    },
  },

  // Claims
  {
    path: '/:organisationId?/claims',
    name: 'claims',
    component: () => import('@/views/orders/ClaimsLayout.vue'),
    meta: {
      title: t('routes.orders.claimsPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.LIST,
      subject: Subjects.ORDER,
    },
  },
  {
    path: '/:organisationId?/claims/create-new-claim',
    name: 'create-new-claim',
    component: () => import('@/views/claims/CreateClaimViewRework.vue'),
    meta: {
      title: t('routes.orders.createNewPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.LIST,
      subject: Subjects.ORDER,
    },
  },
  {
    path: '/:organisationId?/claims/create-new-claim/confirm-requests',
    name: 'confirm-claim-requests',
    component: () => import('@/views/projects/CreateOrderRequests.vue'),
    meta: {
      title: t('routes.projects.confirmRequestsPageTitle'),
      layout: 'default',
      requiresAuth: true,
      action: Actions.LIST,
      subject: Subjects.ORDER,
    },
  },
] as RouteRecordRaw[];
